import "./KPICards.css";
import AnimatedNumber from "./AnimatedNumber";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faWallet,
  faTag,
  faCalendarWeek,
  faUsers,
  faBed,
  faArrowTrendUp,
  faXmark,
  faClock,
  faList,
  faCreditCard,
  faHourglass,
} from "@fortawesome/free-solid-svg-icons";

interface KPICardsProps {
  occupancy: { percent: number; booked: number; total: number };
  revenueMonth: { total: number; nights: number };
  revenueTotal: number;
  bookings: { total: number; active: number; month: number };
  availableRooms: { total: number; ready: number; cleaning: number };
  ADR: number;
  forecast7: { date: string; percent: number }[];
  guestsInHouse: { total: number; checkoutsRemaining: number };
  RevPAR: number;
  bookingPace: number;
  cancellations30: number;
  leadTime: number;
  pendingPayment?: number;
  onHoldRooms?: number;
  loading?: boolean;
}

const CARDS = (p: Omit<KPICardsProps, "loading">) => [
  ...(p.pendingPayment ? [{
    icon: faCreditCard,
    color: "red",
    label: "Pending Payment",
    value: <AnimatedNumber value={p.pendingPayment} />,
    sub: "Checked-in guests with unpaid balance",
    accent: "#ef4444",
    bar: null,
    alert: true,
  }] : []),
  ...(p.onHoldRooms ? [{
    icon: faHourglass,
    color: "orange",
    label: "Rooms On Hold",
    value: <AnimatedNumber value={p.onHoldRooms} />,
    sub: "WhatsApp holds — auto-release after 2 days",
    accent: "#f97316",
    bar: null,
    alert: true,
  }] : []),
  {
    icon: faChartPie,
    color: "blue",
    label: "Today's Occupancy",
    value: <><AnimatedNumber value={p.occupancy.percent} />%</>,
    sub: <><AnimatedNumber value={p.occupancy.booked} /> / <AnimatedNumber value={p.occupancy.total} /> rooms</>,
    accent: "#6366f1",
    bar: p.occupancy.percent,
  },
  {
    icon: faWallet,
    color: "green",
    label: "Total Revenue",
    value: <>EGP <AnimatedNumber value={p.revenueTotal} /></>,
    sub: <>This month: EGP <AnimatedNumber value={p.revenueMonth.total} /> · <AnimatedNumber value={p.revenueMonth.nights} /> nights</>,
    accent: "#10b981",
    bar: null,
  },
  {
    icon: faList,
    color: "cyan",
    label: "Bookings This Month",
    value: <AnimatedNumber value={p.bookings.month} />,
    sub: <><AnimatedNumber value={p.bookings.total} /> total · <AnimatedNumber value={p.bookings.active} /> active</>,
    accent: "#0891b2",
    bar: null,
  },
  {
    icon: faTag,
    color: "gold",
    label: "ADR (Today)",
    value: <>EGP <AnimatedNumber value={p.ADR} /></>,
    sub: "Average rate per sold room",
    accent: "#f59e0b",
    bar: null,
  },
  {
    icon: faBed,
    color: "cyan",
    label: "RevPAR",
    value: <>EGP <AnimatedNumber value={p.RevPAR} /></>,
    sub: "ADR × Occupancy %",
    accent: "#06b6d4",
    bar: null,
  },
  {
    icon: faUsers,
    color: "purple",
    label: "Guests In-House",
    value: <AnimatedNumber value={p.guestsInHouse.total} />,
    sub: <><AnimatedNumber value={p.guestsInHouse.checkoutsRemaining} /> check-outs remaining</>,
    accent: "#8b5cf6",
    bar: null,
  },
  {
    icon: faCalendarWeek,
    color: "blue",
    label: "7-Day Peak Forecast",
    value: <><AnimatedNumber value={Math.max(0, ...p.forecast7.map((d) => d.percent))} />%</>,
    sub: `${p.forecast7.length} days forecasted`,
    accent: "#6366f1",
    bar: Math.max(0, ...p.forecast7.map((d) => d.percent)),
  },
  {
    icon: faArrowTrendUp,
    color: "green",
    label: "Booking Pace (7d)",
    value: <AnimatedNumber value={p.bookingPace} />,
    sub: "New bookings this week",
    accent: "#10b981",
    bar: null,
  },
  {
    icon: faXmark,
    color: "red",
    label: "Cancellations (30d)",
    value: <AnimatedNumber value={p.cancellations30} />,
    sub: "Last 30 days",
    accent: "#ef4444",
    bar: null,
  },
  {
    icon: faClock,
    color: "gold",
    label: "Avg Lead Time",
    value: <><AnimatedNumber value={p.leadTime} /> days</>,
    sub: "Average booking window",
    accent: "#f59e0b",
    bar: null,
  },
];

export default function KPICards({
  occupancy, revenueMonth, revenueTotal, bookings, availableRooms, ADR, forecast7,
  guestsInHouse, RevPAR, bookingPace, cancellations30, leadTime,
  pendingPayment = 0, onHoldRooms = 0,
  loading = false,
}: KPICardsProps) {
  if (loading) {
    return (
      <div className="kpi-grid">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="kpi-card kpi-skeleton" />
        ))}
      </div>
    );
  }

  const cards = CARDS({
    occupancy, revenueMonth, revenueTotal, bookings, availableRooms, ADR, forecast7,
    guestsInHouse, RevPAR, bookingPace, cancellations30, leadTime,
    pendingPayment, onHoldRooms,
  });

  return (
    <div className="kpi-grid">
      {cards.map((card, i) => (
        <div
          key={i}
          className={`kpi-card${(card as any).alert ? " kpi-alert" : ""}`}
          style={{ "--accent": card.accent } as React.CSSProperties}
        >
          <div className={`kpi-icon-wrap kpi-${card.color}`}>
            <FontAwesomeIcon icon={card.icon} className="kpi-fa-icon" />
          </div>
          <p className="kpi-label">{card.label}</p>
          <p className="kpi-value">{card.value}</p>
          <p className="kpi-sub">{card.sub}</p>
          {card.bar !== null && (
            <div className="kpi-bar-track">
              <div
                className="kpi-bar-fill"
                style={{ width: `${Math.min(card.bar, 100)}%`, background: card.accent }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
